import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Tone } from '../theme/tokens';

// Toast + stack (CB-17; VIS §2.2 "Toast"): a glass pill centred at the bottom,
// `toastup` entry, above every layer (z 90). Design system v2 §5 "Toasts confirm
// every mutation". Toasts are announced politely and never take Esc/Back — they are
// not part of the layer stack. An optional single action (e.g. Undo) dismisses the
// toast when pressed. At most MAX_TOASTS show; the oldest drops off first.

export interface ToastInput {
  message: string;
  tone?: Tone;
  action?: { label: string; onAction: () => void };
  /** ms before auto-dismiss; 0 keeps it until dismissed. */
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

export const DEFAULT_TOAST_MS = 4000;
export const MAX_TOASTS = 3;

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setItems((cur) => cur.filter((x) => x.id !== id));
  }, []);

  const show = useCallback(
    (toast: ToastInput) => {
      const id = nextId.current++;
      setItems((cur) => [...cur, { ...toast, id }].slice(-MAX_TOASTS));
      const ms = toast.durationMs ?? DEFAULT_TOAST_MS;
      if (ms > 0) timers.current.set(id, setTimeout(() => dismiss(id), ms));
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((timer) => clearTimeout(timer));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className="toast" data-tone={item.tone ?? 'neutral'} data-motion="">
            <span className="toast__msg">{item.message}</span>
            {item.action && (
              <button
                type="button"
                className="toast__action"
                onClick={() => {
                  item.action?.onAction();
                  dismiss(item.id);
                }}
              >
                {item.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
